import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { EmptyPerks } from "components/Illustrations/EmptyPerks";
import { Form, Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import InfiniteScroll from "react-infinite-scroll-component";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import {
  PerkTag,
  useGetAllPerkTagsQuery,
  useSuggestPerkMutation,
} from "store/apis/perksCrm";
import { useLazyGetAllDealsQuery } from "store/apis/rewards";
import { PerksResponse } from "types/Models/perks";
import { BackendError } from "types/utils/error";
import { useDebounce } from "utils/debounce";
import * as Yup from "yup";
import { PerkCategoryFilter } from "./perkFilter/PerkCategoryFilter";
import { PerkNewCards } from "./PerkNewCard";
import { PerksNewModal } from "./PerksNewModal";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  website: Yup.string()
    .url("Invalid URL format")
    .required("Website is required"),
  notes: Yup.string().required("Note are required"),
});

const SuggestPerk = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [suggestPerk, { isLoading }] = useSuggestPerkMutation();

  const suggestPerkModal = useModal();

  const onSubmit = async (values: {
    name: string;
    website: string;
    notes: string;
  }) => {
    try {
      const { name, website, notes } = values;
      await suggestPerk({ groupId, name, website, notes }).unwrap();
      successToast({
        title: "Suggested Successsfully",
        message: "Perk suggestion shared with the team!",
      });
      suggestPerkModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root
      open={suggestPerkModal.isOpen}
      onOpenChange={suggestPerkModal.toggle}
    >
      <Modal.Trigger asChild>
        <span>
          <Button size="small">Suggest a perk</Button>
        </span>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Suggest a Perk</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik
          onSubmit={onSubmit}
          initialValues={{ name: "", website: "", notes: "" }}
          validationSchema={validationSchema}
        >
          {({ isValid, submitForm }) => (
            <Form className="t-m-0 t-w-full">
              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <TextInput
                  required
                  name="name"
                  label="Name of company"
                  placeholder="Enter company name"
                />
                <TextInput
                  required
                  name="website"
                  label="Company website"
                  placeholder="https://www.example.com"
                />
                <TextArea
                  required
                  name="notes"
                  label="Why do you want this perk?"
                  placeholder="Explain the benefit of this perk for your team"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button" disabled={isLoading}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading || !isValid}
                  onClick={submitForm}
                >
                  Submit
                </Button>
              </Modal.FooterButtonGroup>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};

export const Perks = () => {
  usePageTitle("Perks");
  const { url } = useRouteMatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const queryParam = useQuery();
  const { update } = useUpdateQuery();
  const currentSort = queryParam.get("sort") || "RECENTLY_ADDED";
  const searchQuery = queryParam.get("search") || "";
  const searchTerm = useDebounce(searchQuery);
  const categories = queryParam.get("category")?.split(",").filter(Boolean);

  const updateCategories = (categoryId: string) => {
    const categories = queryParam.get("category")
      ? queryParam.get("category")?.split(",") || []
      : [];
    const index = categories.indexOf(categoryId);
    if (index > -1) {
      categories.splice(index, 1);
    } else {
      categories.push(categoryId);
    }
    update({
      query: "category",
      value: categories.length > 0 ? categories.join(",") : null,
    });
  };

  const updateSort = (sort: string) => {
    update({ query: "sort", value: sort });
  };

  const { data: perkCategories, ...perkCategoriesApiState } =
    useGetAllPerkTagsQuery();

  const {
    data: perksData,
    isLoading,
    isSuccess,
    loadNext,
    pageNum,
  } = usePaginatedQuery<{
    perks: PerksResponse[];
  }>(
    // @ts-ignore
    useLazyGetAllDealsQuery,
    "perks",
    {
      groupId: groupId,
      perk_tag_uuids: categories?.join(","),
      search_term: searchTerm,
      sort_by: currentSort,
    },
    ["groupId"]
  );

  const { perks = [], total_pages = 1 } = perksData || {};

  const setSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search", value: value || null });
  };

  const categoriesIdMap = perkCategories?.reduce((acc, category) => {
    acc[category.uuid] = category;
    return acc;
  }, {} as Record<string, PerkTag | undefined>);

  return (
    <DashboardContainer className="t-h-full t-px-5">
      <DashboardContainer.Header>
        <div className="t-flex t-w-full t-items-center t-justify-between t-gap-3 t-pb-6 sm:t-flex-wrap">
          <Search
            placeholder="Search for a perk"
            onChange={setSearchQuery}
            className="t-w-2/4 sm:t-w-full"
            defaultValue={searchTerm}
          />
          <div className="t-flex t-gap-2 t-items-center">
            <SuggestPerk />
            <ConditionalLink to={`${url}/create`}>
              <Button size="small" customType="primary">
                Submit your perk
              </Button>
            </ConditionalLink>
          </div>
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-h-full" id="scrollableDiv">
        <div className="t-flex t-gap-4 t-items-start sm:t-flex-col sm:t-items-stretch">
          <div className="t-w-64 t-shrink-0 t-bg-surface-lighter-grey t-py-1.5 t-rounded sm:t-w-full sm:t-max-h-64 sm:t-overflow-y-auto">
            <RadioGroup.Root
              className="t-mb-6"
              onValueChange={updateSort}
              value={currentSort}
            >
              <div className="t-py-1.5 t-pt-6 t-px-3">
                <RadioGroup.Item value="RECENTLY_ADDED">
                  <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-h-full">
                    Recently added
                  </span>
                </RadioGroup.Item>
              </div>
              <div className="t-py-1.5 t-px-3">
                <RadioGroup.Item value="MOST_POPULAR">
                  <span className="t-text-body-sm t-text-text-30 t-flex t-items-center t-h-full">
                    Popular
                  </span>
                </RadioGroup.Item>
              </div>
            </RadioGroup.Root>

            <div className="t-m-0 t-px-3 t-mb-1">
              {categories && categories?.length > 0 ? (
                <Checkbox
                  onChange={() => update({ query: "category", value: null })}
                  checked={categories?.length === perkCategories?.length}
                  indeterminate={
                    perkCategories &&
                    categories?.length > 0 &&
                    categories?.length < perkCategories?.length
                  }
                  name="ALL_CATEGORY"
                  label={<span className="t-text-subtitle-sm">Categories</span>}
                />
              ) : (
                <span className="t-text-subtitle-sm">Categories</span>
              )}
            </div>
            <Async.Root
              isEmpty={perkCategories?.length === 0}
              isLoading={perkCategoriesApiState.isLoading}
              isSuccess={perkCategoriesApiState.isSuccess}
              renderOnLoad={
                <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-2 t-px-3 t-py-1.5">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <Skeleton className="t-h-7 t-w-full" key={index} />
                  ))}
                </div>
              }
            >
              <Async.Empty>
                <>No Categories found!</>
              </Async.Empty>
              <Async.Success>
                <div>
                  <PerkCategoryFilter
                    selected={categories || []}
                    onSelect={updateCategories}
                  />
                </div>
              </Async.Success>
            </Async.Root>
          </div>

          <div className="t-grow t-flex t-flex-col t-gap-2">
            <div className="t-flex t-gap-1.5 t-flex-wrap">
              {categories?.map(
                (category) =>
                  categoriesIdMap?.[category] && (
                    <Chip
                      isActive
                      onRemove={() => updateCategories(category)}
                      key={category}
                    >
                      {categoriesIdMap?.[category]?.title}
                    </Chip>
                  )
              )}
            </div>
            <Async.Root
              isEmpty={perks?.length === 0}
              isLoading={isLoading || perksData === undefined}
              isSuccess={isSuccess}
              renderOnLoad={
                <div className="t-flex t-gap-4 t-items-center t-flex-wrap t-max-w-[55rem] t-w-full t-mx-auto">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton
                      className="t-h-[15.625rem] t-w-[30%] t-rounded-lg"
                      key={index}
                    />
                  ))}
                </div>
              }
            >
              <Async.Empty>
                <div className="t-flex t-items-center t-justify-center t-flex-col t-h-96 t-gap-3">
                  <EmptyPerks />
                  <p>No perks found</p>
                  {/* <Button>Suggest a perk</Button> */}
                </div>
              </Async.Empty>
              <Async.Success>
                <>
                  <InfiniteScroll
                    dataLength={perks.length}
                    style={{ flexGrow: 1 }}
                    next={loadNext}
                    hasMore={pageNum < total_pages}
                    scrollableTarget="scrollableDiv"
                    loader={
                      <div className="t-flex t-gap-4 t-items-center t-flex-wrap t-max-w-[55rem] t-w-full t-mx-auto">
                        {Array.from({ length: 6 }).map((_, index) => (
                          <Skeleton
                            className="t-h-[15.625rem] t-w-[30%] t-rounded-lg"
                            key={index}
                          />
                        ))}
                      </div>
                    }
                    className={classNames(
                      "t-flex t-gap-4 t-items-center t-flex-wrap "
                    )}
                  >
                    <div className="t-flex t-gap-4 t-items-center t-flex-wrap t-max-w-[55rem] t-w-full t-mx-auto">
                      {perks?.map((perk) => (
                        <PerkNewCards key={perk?.perk_id} perk={perk} />
                      ))}
                    </div>
                  </InfiniteScroll>
                </>
              </Async.Success>
            </Async.Root>
          </div>
        </div>
      </DashboardContainer.Content>

      <Switch>
        <Route exact path={`${url}/:perkSlug`}>
          <PerksNewModal parentUrl={url || ""} />
        </Route>
      </Switch>
    </DashboardContainer>
  );
};
